import { slicedToArray as _slicedToArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { Box, Button, IconArrowRight } from '@zillow/constellation';
import { useYieldCallback } from '@zillow/yield-callback';
import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect, Fragment } from 'react';
import OverlappingStack from '../PAFormStepper/OverlappingStack.js';
import { ListItemWithProgress, ImageWithProgress } from './ProgressTracker.js';

var MediaStepper = function MediaStepper(_ref) {
  var animationTime = _ref.animationTime,
    buttonText = _ref.buttonText,
    children = _ref.children,
    collapseOthers = _ref.collapseOthers,
    onButtonClick = _ref.onButtonClick,
    reverse = _ref.reverse,
    onStepClick = _ref.onStepClick;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    skipAnimation = _useState2[0],
    setSkipAnimation = _useState2[1];
  var _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    activeNumber = _useState4[0],
    setActiveNumber = _useState4[1];
  var intervalId = useRef(undefined);
  var onPropClick = useYieldCallback(function (propNumber, div) {
    setSkipAnimation(true);
    setActiveNumber(propNumber);
    clearInterval(intervalId.current);
    intervalId.current = undefined;
    onStepClick === null || onStepClick === void 0 || onStepClick(div);
  }, [intervalId.current, onStepClick]);

  // Filter out children that are not wrapped in MediaStepper.Item
  var items = React.Children.toArray(children).filter(function (child) {
    return child.type === MediaStepper.Item;
  });
  useEffect(function () {
    if (!animationTime || animationTime < 0) {
      return;
    }
    intervalId.current = setInterval(function () {
      setActiveNumber(function (prevActiveNumber) {
        return (prevActiveNumber + 1) % items.length;
      });
    }, 1000 * animationTime);
    return function () {
      clearInterval(intervalId.current);
      intervalId.current = undefined;
    };
  }, [animationTime]);
  var onButtonClickYielded = useYieldCallback(function () {
    onButtonClick === null || onButtonClick === void 0 || onButtonClick();
  }, [onButtonClick]);
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Box, {
    sx: {
      gap: '$spacing.sm',
      display: 'grid',
      gridTemplateColumns: '1fr',
      '@media $lg_gte': {
        gap: '$spacing.md',
        gridTemplateColumns: '1fr 1fr'
      }
    }
  }, /*#__PURE__*/React.createElement(Box, {
    sx: Object.assign({}, reverse && {
      order: 1
    }, {
      display: 'flex',
      gap: '$spacing.lg',
      'flex-direction': 'column',
      'margin-right': '$spacing.none',
      '@media $xl_gte': {
        'margin-right': '15%'
      }
    })
  }, items.map(
  // Only take the "heading" and "children" prop from the
  // MediaStepperItem and add the rest of the props that
  // MediaStepperItem needs
  function (item, index) {
    var active = index === activeNumber;
    return /*#__PURE__*/React.createElement(Fragment
    // Safe to use index as it's not dynamic it won't
    // change dynamically except in editor which has
    // re-renders.
    , {
      key: index
    }, /*#__PURE__*/React.createElement(ListItemWithProgress, {
      heading: item.props.heading,
      active: active,
      animationTime: animationTime,
      skipAnimation: skipAnimation,
      onClick: function onClick(div) {
        onPropClick(index, div);
      },
      collapseOthers: collapseOthers
    }, item.props.children));
  }), !!buttonText && /*#__PURE__*/React.createElement(Box, {
    as: Button,
    sx: {
      marginTop: '$spacing.lg !important',
      alignSelf: 'flex-start'
    },
    buttonType: "primary",
    iconPosition: "end",
    icon: /*#__PURE__*/React.createElement(IconArrowRight, null),
    onClick: onButtonClickYielded
  }, buttonText)), /*#__PURE__*/React.createElement(OverlappingStack, {
    sx: Object.assign({}, !reverse && {
      justifyContent: 'end'
    }, {
      '@media $md_lte': {
        display: 'none'
      }
    })
  }, items.map(function (item, index) {
    return /*#__PURE__*/React.createElement(ImageWithProgress
    // Safe to use index as it's not dynamic it won't
    // change dynamically except in editor which has
    // re-renders.
    , {
      key: index,
      active: activeNumber === index,
      media: item.props.image
    });
  }))));
};

// Safe to disable as I define displayName on next line.
// eslint-disable-next-line react/display-name
MediaStepper.Item = function () {
  return null;
};
MediaStepper.Item.displayName = 'MediaStepper.Item';
MediaStepper.Item.propTypes = {
  children: PropTypes.node,
  heading: PropTypes.string.isRequired,
  image: PropTypes.shape({
    alt: PropTypes.string.isRequired,
    src: PropTypes.string.isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired
  }).isRequired
};
MediaStepper.defaultProps = {
  animationTime: 6,
  collapseOthers: false
};
MediaStepper.propTypes = process.env.NODE_ENV !== "production" ? {
  animationTime: PropTypes.number,
  buttonText: PropTypes.string,
  // Only children wrapped with MediaStepper.Item will be rendered
  children: PropTypes.node.isRequired,
  collapseOthers: PropTypes.bool.isRequired,
  heading: PropTypes.string.isRequired,
  onButtonClick: PropTypes.func,
  reverse: PropTypes.bool,
  onStepClick: PropTypes.func
} : {};

/** @component */
var MediaStepper$1 = MediaStepper;

export { MediaStepper$1 as default };
